import {
  ActionMode,
  AnalyticsViewType,
  BaseIntegrationDto,
  CreateExperienceIntegrationDto,
  DeviceType,
  ExperienceAnalysisDto,
  ExperienceDto,
  ExperienceIntegrationDto,
  ExperienceTestTypeDto,
  ExperienceType,
  IntegrationName,
  StayAiExperienceSettingsType,
  StayAiGlobalSettingsType,
  TabType,
  ViewMode,
} from "@intelligems/ig-types";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getExperienceFeFromDto } from "@src/api/formatters/from-dto/experiences";
import { initialExperienceFeDto } from "@src/api/initials";

import { ExperienceDetails, IExperienceState } from "./types";

export const initialState: IExperienceState = {
  isSuccess: false,
  experience: undefined,
  storedExperience: undefined,
  hasClickedSave: false,
  pageContentRef: null,
};

const experienceSlice = createSlice({
  name: "experience",
  initialState,
  reducers: {
    initExperimentFromDto: (
      draft,
      action: PayloadAction<{
        experience?: ExperienceDto;
        integrations?: BaseIntegrationDto[];
        testFlowType: ExperienceType;
        defaultCurrency?: string;
        name?: string;
        description?: string;
        tab?: TabType;
      }>
    ) => {
      draft.isSuccess = true;
      const {
        testFlowType,
        experience,
        integrations,
        defaultCurrency,
        name,
        description,
      } = action.payload;
      if (experience) {
        draft.experience = getExperienceFeFromDto(experience);
        draft.experience.actionMode = "edit";
        if (action.payload.tab) {
          draft.experience.tab = action.payload.tab;
        }
      } else {
        draft.experience = initialExperienceFeDto({
          defaultCurrency,
          tab: action.payload.tab,
          hasTestPricing: testFlowType === "pricing",
          hasTestShipping: testFlowType === "shipping",
          hasTestCampaign: testFlowType === "personalization/offer",
          hasTestContent: true,
          testFlowType: action.payload.testFlowType,
          useWideApp: false,
          hasTestContentAdvanced:
            action.payload.testFlowType === "content/advanced",
          hasTestContentUrl: action.payload.testFlowType === "content/url",
          hasTestContentOnsite:
            action.payload.testFlowType === "content/onsiteEdits",
          hasTestContentTemplate:
            action.payload.testFlowType === "content/template",
          hasTestContentTheme: action.payload.testFlowType === "content/theme",
          hasTestOnsiteInjections: false,
          category: "experiment",
          name,
          description,
          integrations,
        });
      }
      draft.storedExperience = draft.experience;
    },
    initPersonalizationFromDto: (
      draft,
      action: PayloadAction<ExperienceDto | undefined>
    ) => {
      draft.isSuccess = true;
      if (action.payload) {
        draft.experience = getExperienceFeFromDto(action.payload);
        draft.experience.actionMode = "edit";
        draft.experience.viewMode = action.payload.archivedAtTs
          ? "view"
          : "edit";
      } else {
        draft.experience = initialExperienceFeDto({
          testFlowType: "personalization",
          useWideApp: false,
          defaultCurrency: undefined,
          hasTestPricing: false,
          hasTestShipping: false,
          hasTestCampaign: false,
          hasTestContent: false,
          hasTestContentAdvanced: false,
          hasTestContentUrl: false,
          hasTestContentOnsite: false,
          hasTestContentTemplate: false,
          hasTestContentTheme: false,
          category: "personalization",
          hasTestOnsiteInjections: false,
        });
        draft.experience.actionMode = "create";
      }
      draft.storedExperience = draft.experience;
    },
    resetExperience: () => initialState,
    setExperienceId: (draft, action: PayloadAction<string | undefined>) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.id = action.payload;
    },
    setExperienceTabWithoutRedirect: (
      draft,
      action: PayloadAction<TabType>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.tab = action.payload;
    },
    setPageRefObj: (draft, action: PayloadAction<any>) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.pageContentRef = action.payload;
    },
    setExperienceTab: (
      draft,
      action: PayloadAction<
        TabType | { tab: TabType; mode: "push" | "replace" }
      >
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      const { tab, mode } =
        typeof action.payload === "string"
          ? {
              tab: action.payload,
              mode: "push",
            }
          : action.payload;

      draft.experience.tab = tab;
      const url = new URL(window.location.href);
      url.searchParams.set("tab", tab);
      if (mode === "push") {
        window.history.pushState(null, "", url.toString());
      } else {
        window.history.replaceState(null, "", url.toString());
      }
    },
    initExperienceDetails: (
      draft,
      action: PayloadAction<ExperienceDetails>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      if (action.payload.name !== undefined) {
        draft.experience.name = action.payload.name;
      }
      if (action.payload.description !== undefined) {
        draft.experience.description = action.payload.description;
      }
      draft.storedExperience = draft.experience;
    },
    setExperienceDetails: (draft, action: PayloadAction<ExperienceDetails>) => {
      if (!draft.isSuccess || !draft.experience) return;

      if (action.payload.name !== undefined) {
        draft.experience.name = action.payload.name;
      }
      if (action.payload.description !== undefined) {
        draft.experience.description = action.payload.description;
      }
    },
    setExperienceGoal: (draft, action: PayloadAction<string>) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.experienceAnalysis.goal = action.payload;
    },
    setExperienceViewMode: (draft, action: PayloadAction<ViewMode>) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.viewMode = action.payload;
    },
    setExperienceActionMode: (draft, action: PayloadAction<ActionMode>) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.actionMode = action.payload;
    },
    setExperienceDevicePreview: (
      draft,
      action: PayloadAction<{
        device?: DeviceType;
        path?: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      if (action.payload.device) {
        if (draft.experience?.devicePreview) {
          draft.experience.devicePreview.device = action.payload.device;
        } else {
          draft.experience.devicePreview = {
            device: action.payload.device,
            path: "/",
          };
        }
      }

      if (action.payload.path !== undefined) {
        if (draft.experience?.devicePreview) {
          draft.experience.devicePreview.path = action.payload.path
            ? action.payload.path.startsWith("/")
              ? action.payload.path
              : `/${action.payload.path}`
            : "/";
        } else {
          draft.experience.devicePreview = {
            device: "desktop",
            path: action.payload.path
              ? action.payload.path.startsWith("/")
                ? action.payload.path
                : `/${action.payload.path}`
              : "/",
          };
        }
      }
      if (draft.storedExperience) {
        draft.storedExperience.devicePreview = draft.experience.devicePreview;
      }
    },
    setExperienceCurrency: (
      draft,
      action: PayloadAction<{
        code: string;
        symbol: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.currency = action.payload;
    },
    setExperienceShippingProfile: (
      draft,
      action: PayloadAction<{
        deliveryProfileId: string;
        deliveryLocationGroupId: string;
        deliveryZoneId: string;
        methodDefinitionId: string | null;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      if (draft.experience.shippingTestMethodDefinitions.length < 1) {
        draft.experience.shippingTestMethodDefinitions.push({
          deliveryProfileId: action.payload.deliveryProfileId,
          deliveryZoneId: action.payload.deliveryZoneId,
          deliveryLocationGroupId: action.payload.deliveryLocationGroupId,
          methodDefinitionId: action.payload.methodDefinitionId,
        });
      } else {
        const zoneIndex =
          draft.experience.shippingTestMethodDefinitions.findIndex(
            (def) => def.deliveryZoneId === action.payload.deliveryZoneId
          );
        if (zoneIndex === -1) {
          draft.experience.shippingTestMethodDefinitions.push({
            deliveryProfileId: action.payload.deliveryProfileId,
            deliveryLocationGroupId: action.payload.deliveryLocationGroupId,
            deliveryZoneId: action.payload.deliveryZoneId,
            methodDefinitionId: action.payload.methodDefinitionId,
          });
        } else {
          draft.experience.shippingTestMethodDefinitions.splice(zoneIndex, 1);
        }
      }
    },
    setExperienceShippingTestMethodDefinition: (
      draft,
      action: PayloadAction<{
        deliveryProfileId: string;
        deliveryLocationGroupId: string;
        deliveryZoneId: string;
        methodDefinitionId: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      const deliveryZoneIndex =
        draft.experience.shippingTestMethodDefinitions.findIndex(
          (def) =>
            def.deliveryZoneId === action.payload.deliveryZoneId &&
            !def.methodDefinitionId
        );

      const methodDefIndex =
        draft.experience.shippingTestMethodDefinitions.findIndex(
          (def) => def.methodDefinitionId === action.payload.methodDefinitionId
        );
      if (methodDefIndex === -1) {
        // method def needs to be added
        if (deliveryZoneIndex === -1) {
          draft.experience.shippingTestMethodDefinitions.push({
            deliveryProfileId: action.payload.deliveryProfileId,
            deliveryLocationGroupId: action.payload.deliveryLocationGroupId,
            deliveryZoneId: action.payload.deliveryZoneId,
            methodDefinitionId: action.payload.methodDefinitionId,
          });
        } else {
          draft.experience.shippingTestMethodDefinitions[
            deliveryZoneIndex
          ].methodDefinitionId = action.payload.methodDefinitionId;
        }
      } else {
        // remove
        draft.experience.shippingTestMethodDefinitions[methodDefIndex] = {
          ...draft.experience.shippingTestMethodDefinitions[methodDefIndex],
          methodDefinitionId: null,
        };
      }
    },
    setExperienceAnalyticsViewType: (
      draft,
      action: PayloadAction<AnalyticsViewType>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.experienceAnalysis.analyticsViewType = action.payload;
    },
    toggleRequiresLink: (
      draft,
      action: PayloadAction<{ requiresLink: boolean }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.requiresLink = action.payload.requiresLink;
      return draft;
    },
    setLinkBaseUrl: (draft, action: PayloadAction<{ linkBaseUrl: string }>) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.linkBaseUrl = action.payload.linkBaseUrl;
      return draft;
    },
    deleteShippingExpMethodDefinitionById: (
      draft,
      action: PayloadAction<string>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      const methodDefIndexRemove =
        draft.experience.shippingTestMethodDefinitions.findIndex(
          (def) => def.methodDefinitionId === action.payload
        );
      draft.experience.shippingTestMethodDefinitions[
        methodDefIndexRemove
      ].methodDefinitionId = null;

      draft.experience.shippingTestMethodDefinitions.filter(
        (thing, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.deliveryZoneId === thing.deliveryZoneId &&
              t.methodDefinitionId === thing.methodDefinitionId
          )
      );
    },
    resetTestType: (
      draft,
      action: PayloadAction<{
        testType: keyof ExperienceTestTypeDto;
        value: boolean;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.testTypes[action.payload.testType] =
        action.payload.value;
    },
    resetExperienceAnalysisToStored: (draft) => {
      if (!draft.isSuccess || !draft.experience || !draft.storedExperience)
        return;

      draft.experience.experienceAnalysis.analyticsViewType =
        draft.storedExperience.experienceAnalysis.analyticsViewType;
    },
    setExperienceIntegration: (
      draft,
      action: PayloadAction<{
        name: IntegrationName;
        experienceSettings: StayAiExperienceSettingsType;
        enabled: boolean;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      if (!draft.experience.experienceIntegrations) {
        draft.experience.experienceIntegrations = [];
      }

      if (action.payload.name === "StayAi") {
        const stayAi = (
          draft.experience.experienceIntegrations || []
        ).findIndex((ei) => ei.integration.name === "StayAi");

        if (stayAi > -1) {
          draft.experience.experienceIntegrations[stayAi].experienceSettings =
            action.payload.experienceSettings;
          draft.experience.experienceIntegrations[stayAi].enabled =
            action.payload.enabled;
        } else {
          draft.experience.experienceIntegrations.push({
            integration: {
              enabled: true,
              description: "Stay.ai",
              name: "StayAi",
              apis: [],
              globalSettings: {},
            },
            enabled: action.payload.enabled,
            experienceSettings: action.payload.experienceSettings,
          });
        }
      }
    },
    initExperienceAnalysis: (
      draft,
      action: PayloadAction<{
        experienceAnalysis: ExperienceAnalysisDto;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return;

      draft.experience.experienceAnalysis = action.payload.experienceAnalysis;
      if (draft.storedExperience) {
        draft.storedExperience.experienceAnalysis =
          action.payload.experienceAnalysis;
      }
    },
    setHasClickedSave: (draft, action: PayloadAction<boolean>) => {
      draft.hasClickedSave = action.payload;
    },
  },
  selectors: {
    selectedExperienceState: (state) => state,
    selectedExperience: (state) => state.experience as ExperienceFe,
    selectedStoredExperience: (state) => state?.storedExperience,
    selectedExperienceStayAiIntegration: (state) => {
      return (
        (state.experience as ExperienceFe).experienceIntegrations || []
      ).find((ei) => ei.integration.name === "StayAi") as
        | ExperienceIntegrationDto<
            "StayAi",
            StayAiGlobalSettingsType,
            StayAiExperienceSettingsType
          >
        | undefined;
    },
    hasClickedSave: (state) => state.hasClickedSave,
  },
});

export const {
  resetExperience,
  initExperimentFromDto,
  initExperienceDetails,
  setExperienceId,
  setExperienceTabWithoutRedirect,
  setExperienceTab,
  setExperienceDetails,
  setExperienceGoal,
  setExperienceViewMode,
  setExperienceActionMode,
  setExperienceDevicePreview,
  setExperienceCurrency,
  setExperienceShippingProfile,
  setExperienceShippingTestMethodDefinition,
  setExperienceAnalyticsViewType,
  setExperienceIntegration,
  deleteShippingExpMethodDefinitionById,
  toggleRequiresLink,
  setLinkBaseUrl,
  initPersonalizationFromDto,
  resetTestType,
  resetExperienceAnalysisToStored,
  initExperienceAnalysis,
  setHasClickedSave,
  setPageRefObj,
} = experienceSlice.actions;

export const {
  selectedExperienceState,
  selectedExperience,
  selectedStoredExperience,
  selectedExperienceStayAiIntegration,
  hasClickedSave,
} = experienceSlice.selectors;

export const experienceReducer = experienceSlice.reducer;
